import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePOSLogic from "./logic/usePOSLogic.js";
import "./css/pos.css";
import "./css/locationContainer.css";
import "./css/customprod.css";
import { createPortal } from "react-dom";
import CustomSelect from "../globalcontrols/customselect/customselect.js";
import ConfirmDialog from "../globalcontrols/confirmdialog/ConfirmDialog";
import { API_BASE } from "../../config/api";
import { isWeightedUnit, normalizeSaleUnit, priceUnitLabel, stockUnitLabel } from "./logic/saleUnits";

function TerminalReconciliation({ onVerify }) {
  const [orderId, setOrderId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return <form onSubmit={async (event) => {
    event.preventDefault(); if (busy) return; setBusy(true); setError('');
    try { await onVerify(orderId.trim()); } catch (failure) { setError(failure.message); } finally { setBusy(false); }
  }}>
    <label>ID de orden en Mercado Pago<input required value={orderId} onChange={(event) => setOrderId(event.target.value)} /></label>
    {error && <p role="alert">{error}</p>}
    <button type="submit" disabled={busy}>{busy ? 'Verificando…' : 'Verificar orden'}</button>
  </form>;
}

function POS() {
  const {
    // refs
    searchContainerRef,

    // state
    searchResults, setSearchResults,
    searchQuery, setSearchQuery,
    cart, setCart,
    total,
    showPaymentModal, setShowPaymentModal,
    showCartEmptyAlert, setShowCartEmptyAlert,
    workerCode, setWorkerCode,
    workerName,
    workerCodeError,
    cashAmount, setCashAmount,
    cardAmount, setCardAmount,
    cashGiven, setCashGiven,
    cardGiven, setCardGiven,
    showGenericModal, setShowGenericModal,
    genericModalContent,
    availableLocations,
    selectedLocation, setSelectedLocation,
    setIsLocationDropdownOpen,
    pendingActivation,
    activatePendingLocation,
    showLocationSelect,
    locationOptions, noAvailableLocations, setNoAvailableLocations,
    forceCloseTarget, setForceCloseTarget,
    selectAndActivateLocation,
    pendingLocationChange, setPendingLocationChange,
    showLocationChangeModal, setShowLocationChangeModal,
    workerDiscount,
    showCustomProductModal, setShowCustomProductModal,
    customProduct, setCustomProduct,
    qtyInputs,

    // payment machines state
    paymentMachines,
    machinesLoading,
    selectedMachineId, setSelectedMachineId,
    sendingPayment,
    confirmedCardTotal,
    noTicket, setNoTicket,
    manualWithoutMachine, setManualWithoutMachine,
    canConfirm,
    submittingSale,

    // derived
    discountedTotal,
    totalEntered,
    remaining,
    posSidesWidth,

    // handlers / logic
    searchProduct,
    addProductToCart,
    removeProductFromCart,
    incrementQty,
    decrementQty,
    handleQtyChange,
    parseAmount,
    resetPaymentState,
    handleSubmitSale,
    showModal,

    // payment machines
    sendCardPaymentToMachine,
  } = usePOSLogic();

  // Temp selection for location chooser
  const [tempLoc, setTempLoc] = useState(null);
  const navigate = useNavigate();
  const [differentBalance, setDifferentBalance] = useState(false);
  const [physicalBalance, setPhysicalBalance] = useState('');
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [expectedCloseBalance, setExpectedCloseBalance] = useState(0);
  const [postCloseActivate, setPostCloseActivate] = useState(false);
  const [closeTargetLocationId, setCloseTargetLocationId] = useState(null);
  const [showForcePrompt, setShowForcePrompt] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustType, setAdjustType] = useState('add'); // 'add' or 'retire'
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);

  // When hook signals an occupied location (forceCloseTarget), ask if force close
  useEffect(() => {
    if (!forceCloseTarget) return;
    setShowForcePrompt(true);
  }, [forceCloseTarget, setForceCloseTarget]);

  // No auto-redirect; user must confirm in the warning dialog

  // Money formatting helper for CLP
  const formatMoney = (n) => {
    const num = Number.isFinite(Number(n)) ? Number(n) : 0;
    return `$${Math.round(num).toLocaleString('de-DE')}`;
  };

  // Money already valid to close the bill:
  const tendered = confirmedCardTotal + totalEntered;
  // 🚫 Lock card controls if there’s a confirmed (accredited) card payment and the bill isn’t fully paid
  const cardLocked = confirmedCardTotal > 0 && confirmedCardTotal < discountedTotal;

  // 🔒 Lock cash controls when the card textbox covers the entire bill
  const lockCash = cardAmount >= discountedTotal;

  // 🔒 Lock card-side UI if CASH already covers the entire bill
  const cashCoversBill = cashAmount >= discountedTotal;

  // Card UI is hard-locked if card is locked or cash covers the bill
  const cardUIHardLocked = cardLocked || cashCoversBill;

  const pendingForCardAfterTyped = Math.max(
    discountedTotal - (cashAmount + confirmedCardTotal + cardAmount),
    0
  );

  // One place to drive the import-to-card lock
  const disableCardImport = cardLocked || cashCoversBill || pendingForCardAfterTyped <= 0;

  function confirmRemoveCartItem(item) {
    setConfirmDialog({
      title: "Eliminar producto",
      message: `Eliminar "${item.description || item.name || "este producto"}" del carrito?`,
      confirmLabel: "Eliminar",
      onConfirm: () => {
        removeProductFromCart(item.id);
        setConfirmDialog(null);
      },
    });
  }

  function confirmClearCart() {
    if (!cart.length) return;
    setConfirmDialog({
      title: "Borrar carrito",
      message: "Eliminar todos los productos del carrito actual?",
      confirmLabel: "Borrar carrito",
      onConfirm: () => {
        setCart([]);
        setConfirmDialog(null);
      },
    });
  }



  return (
    <>
      <div id="pos-container">
        {/* Product Finder */}
        <div id="pos-top-container">
          <div id="pos-top-left-container" style={{ width: posSidesWidth }}>
            <div className="pos-location-wrapper">
              <div className="pos-location-simple-display">
                {selectedLocation ? (availableLocations[selectedLocation] || 'Ubicación') : 'Selecciona ubicación'}
              </div>
            </div>
          </div>

          <div id="pos-top-center-container" ref={searchContainerRef}>
            <div id="product-finder">
              <div id="finder-input-container">
                <input
                  type="text"
                  id="finder-input"
                  placeholder="Busca un producto"
                  value={searchQuery}
                  autoComplete="off"
                  disabled={!selectedLocation}
                  onChange={(e) => {
                    const query = e.target.value;
                    setSearchQuery(query);
                    searchProduct(query);
                  }}
                />
              </div>
              <div id="finder-button">
                <span className="material-symbols-outlined" id="find-icon">
                  search
                </span>
              </div>
            </div>

            {/* Search results */}
            {searchQuery.trim() !== "" && (
              <div id="product-finder-results">
                {searchResults.length === 0 ? (
                  <div className="result-item">No se encontraron resultados</div>
                ) : (
                  searchResults.map((product) => (
                    <div
                      key={product.id}
                      className="result-item"
                      onClick={() => {
                        addProductToCart(product);
                        setSearchQuery("");
                        setSearchResults([]);
                      }}
                    >
                      <div className="result-item-data result-name" title={product.description}>
                        {product.description}
                      </div>
                      <div className="result-item-data result-reqQty">Cantidad: {product.qty} {stockUnitLabel(product.sale_unit)}</div>
                      <div className="result-item-data result-price">
                        Precio: ${parseInt(product.unit_price).toLocaleString("de-DE")} {priceUnitLabel(product.sale_unit)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div id="pos-top-right-container" style={{ width: posSidesWidth }}></div>
        </div>

        {/* Cart Section */}
        <div id="pos-center-container">
          <div id="pos-center-left-container" style={{ width: posSidesWidth }}></div>

          <div id="pos-center-middle-container">
            <div id="cart-container">
              <div id="cart-header-container">
                <div id="cart-header">
                  <div id="cart-header-text">
                    <div className="cart-header-content">Nombre</div>
                    <div className="cart-header-content cart-data-element-center">Disponible</div>
                    <div className="cart-header-content cart-data-element-center">Cantidad</div>
                    <div className="cart-header-content cart-data-element-center">Precio</div>
                    <div className="cart-header-content cart-data-element-center">Unidad</div>
                  </div>
                  <div id="cart-header-text-dummy"></div>
                </div>
              </div>

              <div id="cart-content-scroll-wrapper">
                <div id="cart-content">
                  {cart.map((item) => (
                    <div key={item.id} className="cart-item" code={item.code}>
                      <div className="cart-item-text">
                        <div className="cart-item-data" title={item.description}>
                          <div className="cart-item-data-description">{item.description}</div>
                        </div>

                        <div className="cart-item-data cart-data-element-center">{item.qty} {stockUnitLabel(item.sale_unit)}</div>

                        <div className="cart-item-data cart-data-element-center">
                          <div className="cart-item-data-reqqty">
                            <div
                              className="cart-item-reqqty-button"
                              onClick={() => decrementQty(item.code)}
                              style={{
                                opacity: isWeightedUnit(item.sale_unit) ? 0.3 : 1,
                                pointerEvents: isWeightedUnit(item.sale_unit) ? "none" : "auto",
                              }}
                            >
                              -
                            </div>

                            <input
                              code={item.code}
                              className="cart-item-reqqty"
                              value={qtyInputs[item.code] ?? item.reqQty.toString()}
                              type="text"
                              inputMode={normalizeSaleUnit(item.sale_unit) === "kg" ? "decimal" : "numeric"}
                              onChange={(e) => handleQtyChange(e, item.code, item)}
                            />

                            <div
                              className="cart-item-reqqty-button"
                              onClick={() => incrementQty(item.code)}
                              style={{
                                opacity: isWeightedUnit(item.sale_unit) ? 0.3 : 1,
                                pointerEvents: isWeightedUnit(item.sale_unit) ? "none" : "auto",
                              }}
                            >
                              +
                            </div>
                          </div>
                        </div>

                        <div className="cart-item-data cart-data-element-center">
                          ${parseInt(item.unit_price).toLocaleString("de-DE")} {priceUnitLabel(item.sale_unit)}
                        </div>
                        <div className="cart-item-data cart-data-element-center">{stockUnitLabel(item.sale_unit)}</div>
                      </div>

                      <div className="cart-item-delete">
                        <div onClick={() => confirmRemoveCartItem(item)} className="cart-item-delete-button">
                          <span className="material-symbols-outlined">delete</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div id="cart-footer">
                <div id="cart-total">
                  <div id="cart-total-label">Total:&nbsp;</div>
                  <div id="cart-total-amount">${Math.round(total).toLocaleString("de-DE")}</div>
                  <span className="material-symbols-outlined cart-icon">shopping_cart</span>
                </div>
              </div>
            </div>
          </div>

          <div id="pos-center-right-container" style={{ width: posSidesWidth }}>
            <div
              id="add-custom-product-button"
              className="pos-center-right-container-element"
              title="Agregar producto personalizado"
              onClick={() => {
                setCustomProduct({ description: "", unit_price: 0, reqQty: 1 });
                setShowCustomProductModal(true);
              }}
            >
              <span className="material-symbols-outlined right-side-pos-buttons">add</span>
            </div>

            <div id="delete-complete-cart-button" className="pos-center-right-container-element" title="Borrar carrito" onClick={confirmClearCart}>
              <span className="material-symbols-outlined right-side-pos-buttons">delete</span>
            </div>

            <div
              id="payment-cart-button"
              className="pos-center-right-container-element"
              title="Ir a ventana de pago"
              onClick={() => {
                if (cart.length > 0) {
                  const invalidItems = cart.filter((item) => {
                    const quantity = Number(item.reqQty);
                    const available = Number(item.qty);
                    const custom = String(item.code || "").startsWith("custom-");
                    return !Number.isFinite(quantity) || quantity <= 0 || (!custom && Number.isFinite(available) && quantity > available);
                  });
                  if (invalidItems.length > 0) {
                    showModal({
                      title: "Cantidad requerida",
                      message: "Revisa que todos los productos tengan una cantidad válida y no superen el stock disponible.",
                      onClose: () => setShowGenericModal(false),
                    });
                    return;
                  }
                  setShowPaymentModal(true);
                } else {
                  setShowCartEmptyAlert(true);
                }
              }}
            >
              <span className="material-symbols-outlined right-side-pos-buttons">payment</span>
            </div>

            {/* Add cash button */}
            <div
              id="add-cash-button"
              className="pos-center-right-container-element"
              title="Agregar efectivo"
              onClick={() => {
                if (!selectedLocation) { alert('Selecciona una ubicación'); return; }
                setAdjustType('add');
                setAdjustAmount('');
                setAdjustReason('');
                setShowAdjustModal(true);
              }}
            >
              <span className="material-symbols-outlined right-side-pos-buttons">attach_money</span>
            </div>

            {/* Retire cash button */}
            <div
              id="retire-cash-button"
              className="pos-center-right-container-element"
              title="Retirar efectivo"
              onClick={() => {
                if (!selectedLocation) { alert('Selecciona una ubicación'); return; }
                setAdjustType('retire');
                setAdjustAmount('');
                setAdjustReason('');
                setShowAdjustModal(true);
              }}
            >
              <span className="material-symbols-outlined right-side-pos-buttons">money_off</span>
            </div>

            {/* Close POS button */}
            <div
              id="close-pos-button"
              className="pos-center-right-container-element"
              onClick={async () => {
                try {
                  if (!selectedLocation) { alert('Selecciona una ubicación'); return; }
                  const sessionData = JSON.parse(localStorage.getItem('sessionData'));
                  const res = await fetch(`${API_BASE}/locations/${selectedLocation}/state`, {
                    method:'GET', headers: { Authorization: sessionData.sessionKey, Username: sessionData.username }
                  });
                  if (!res.ok) { alert('No se pudo obtener el estado de caja'); return; }
                  const data = await res.json();
                  const bal = Number(data?.location?.actual_balance || 0);
                  setExpectedCloseBalance(bal);
                  setDifferentBalance(false);
                  setPhysicalBalance(String(Math.round(bal)));
                  setCloseTargetLocationId(String(selectedLocation));
                  setPostCloseActivate(false);
                  setShowCloseModal(true);
                } catch (e) {
                  console.error('open close modal error', e);
                  alert('Error al abrir cierre de caja');
                }
              }}
              title="Cerrar caja"
            >
              <span className="material-symbols-outlined right-side-pos-buttons">logout</span>
            </div>

          </div>
        </div>
      </div>

      {/* Cart Empty Modal */}
      {showCartEmptyAlert &&
        createPortal(
          <div id="cart-empty-modal-overlay" onClick={() => setShowCartEmptyAlert(false)}>
            <div id="cart-empty-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>El carrito está vacío</h3>
              <p>Por favor agrega productos antes de pagar.</p>
              <button onClick={() => setShowCartEmptyAlert(false)}>Cerrar</button>
            </div>
          </div>,
          document.getElementById("modal-root")
        )}

  {/* Generic Modal */}
      {showGenericModal &&
        createPortal(
          <div
            id="cart-empty-modal-overlay"
            onClick={() => {
              setShowGenericModal(false);
              if (genericModalContent.onClose) genericModalContent.onClose();
            }}
          >
            <div id="cart-empty-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>{genericModalContent.title}</h3>
              <p>{genericModalContent.message}</p>
              {genericModalContent.recover && <button type="button" onClick={genericModalContent.recover}>Recuperar venta</button>}
              {genericModalContent.reconcile && <TerminalReconciliation key={genericModalContent.message} onVerify={genericModalContent.reconcile} />}
              <button
                onClick={() => {
                  setShowGenericModal(false);
                  if (genericModalContent.onClose) genericModalContent.onClose();
                }}
              >
                Cerrar
              </button>
            </div>
          </div>,
          document.getElementById("modal-root")
        )}

      {/* Location selection modal (shown on POS entry if user has multiple) */}
      {showLocationSelect &&
        createPortal(
          (function() {
            const opts = Array.isArray(locationOptions) && locationOptions.length
              ? locationOptions
              : Object.entries(availableLocations).map(([id,name])=>({id,name}));
            const firstId = opts.length ? opts[0].id : null;
            const selectedVal = tempLoc ?? firstId;
            return (
              <div id="cart-empty-modal-overlay">
                <div id="cart-empty-modal-content" style={{ width:'360px', maxWidth:'92vw' }}>
                  <h3>Selecciona ubicación</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ maxWidth: 360, alignSelf: 'center', width: '100%' }}>
                      <CustomSelect
                        label="Ubicación"
                        options={opts}
                        selectedValue={selectedVal}
                        onChange={(val) => setTempLoc(val)}
                        labelExtractor={(o) => o.name}
                        valueExtractor={(o) => o.id}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                      <button onClick={() => navigate('/app/home')}>Cancelar</button>
                      <button
                        onClick={() => {
                          const id = selectedVal;
                          if (id) selectAndActivateLocation(id);
                        }}
                      >
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })(),
          document.getElementById('modal-root')
        )}

      {/* Force close confirmation when location is occupied */}
      {showForcePrompt && forceCloseTarget &&
        createPortal(
          <div id="cart-empty-modal-overlay">
            <div id="cart-empty-modal-content">
              <h3>Caja ocupada</h3>
              <p>El usuario <strong>{forceCloseTarget.active_user}</strong> se encuentra conectado en esta caja.</p>
              <p>¿Desea forzar el cierre?</p>
              <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                <button onClick={() => { setShowForcePrompt(false); setForceCloseTarget(null); navigate('/app/home'); }}>Cancelar</button>
                <button onClick={() => {
                  // Proceed to close modal without cancel option
                  setExpectedCloseBalance(Number(forceCloseTarget.expected_balance || 0));
                  setPhysicalBalance(String(Math.round(Number(forceCloseTarget.expected_balance || 0))));
                  setDifferentBalance(false);
                  setCloseTargetLocationId(String(forceCloseTarget.id));
                  setPostCloseActivate(true);
                  setShowForcePrompt(false);
                  setForceCloseTarget(null);
                  setShowCloseModal(true);
                }}>Forzar cierre</button>
              </div>
            </div>
          </div>,
          document.getElementById('modal-root')
        )}

      {noAvailableLocations &&
        createPortal(
          <div id="cart-empty-modal-overlay">
            <div id="cart-empty-modal-content">
              <h3>Sin cajas disponibles</h3>
              <p>No hay cajas disponibles para tus ubicaciones asignadas en este momento.</p>
              <p>Si una caja está en uso por otro usuario, inténtalo más tarde.</p>
              <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                <button onClick={() => { setNoAvailableLocations(false); navigate('/app/home'); }}>OK</button>
              </div>
            </div>
          </div>,
          document.getElementById('modal-root')
        )}

      {/* auto-redirect handled by component-level useEffect above */}

      {/* Balance confirmation modal (custom dialog) */}
      {pendingActivation &&
        createPortal(
          <div id="cart-empty-modal-overlay">
            <div id="cart-empty-modal-content">
              {differentBalance ? (
                <>
                  <h3>Actualización de Caja</h3>
                  <p style={{ fontSize: '16px' }}>Por favor ingresa el monto actualizado de la caja</p>
                </>
              ) : (
                <>
                  <h3>Confirmar saldo de caja</h3>
                  <p style={{ fontSize: '18px', fontWeight: 700 }}>
                    Saldo en sistema: {formatMoney(Number(pendingActivation.actual_balance || 0))}
                  </p>
                </>
              )}
              {!differentBalance ? (
                <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                  <button onClick={() => navigate('/app/home')}>Cancelar</button>
                  <button onClick={() => setDifferentBalance(true)}>Modificar</button>
                  <button onClick={() => activatePendingLocation({ confirmSame: true })}>OK</button>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  <label style={{ textAlign:'center', display:'block' }}>Saldo físico actual</label>
                  <input
                    type="text"
                    value={formatMoney((parseInt(physicalBalance || '0', 10)))}
                    onChange={(e) => {
                      // parseAmount from hook
                      const val = parseAmount(e.target.value);
                      setPhysicalBalance(String(val));
                    }}
                    placeholder="Ingresa el saldo físico"
                    style={{ fontSize: '18px', fontWeight: 600, display:'block', margin:'0 auto', textAlign:'center', width:'240px' }}
                  />
                  <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                    <button onClick={() => { setDifferentBalance(false); setPhysicalBalance(''); }}>Atrás</button>
                    <button onClick={() => navigate('/app/home')}>Cancelar</button>
                    <button
                      onClick={() => {
                        const val = parseAmount(physicalBalance);
                        if (!Number.isFinite(val) || val < 0) { alert('Monto inválido'); return; }
                        activatePendingLocation({ confirmSame: false, physicalBalance: val });
                        setDifferentBalance(false);
                        setPhysicalBalance('');
                      }}
                    >
                      OK
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.getElementById('modal-root')
        )}

      {/* Close POS modal */}
      {showCloseModal &&
        createPortal(
          <div id="cart-empty-modal-overlay">
            <div id="cart-empty-modal-content">
              {differentBalance ? (
                <>
                  <h3>Actualización de Caja</h3>
                  <p style={{ fontSize: '16px' }}>Por favor ingresa el monto actualizado de la caja</p>
                </>
              ) : (
                <>
                  <h3>Cierre de Caja</h3>
                  <p style={{ fontSize: '18px', fontWeight: 500 }}>
                    Confirma que el saldo en caja es de: {formatMoney(Number(expectedCloseBalance||0))}
                  </p>
                </>
              )}
              {!differentBalance ? (
                <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                  <button onClick={() => { setShowCloseModal(false); if (postCloseActivate) navigate('/app/home'); }}>Cancelar</button>
                  <button onClick={() => setDifferentBalance(true)}>Modificar</button>
                  <button onClick={async () => {
                    try {
                      const sessionData = JSON.parse(localStorage.getItem('sessionData'));
                      const body = {
                        location_id: Number(closeTargetLocationId || selectedLocation),
                        physical_cash: Number(expectedCloseBalance||0),
                        ...(postCloseActivate ? { force: true } : {})
                      };
                      const resp = await fetch(`${API_BASE}/locations/close`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization: sessionData.sessionKey, Username: sessionData.username }, body: JSON.stringify(body) });
                      if (!resp.ok) { const t = await resp.text(); alert(`No se pudo cerrar la caja. ${t}`); return; }
                      setShowCloseModal(false);
                      if (postCloseActivate && closeTargetLocationId) {
                        // Activate directly without extra prompt (confirm same)
                        const body2 = { location_id: Number(closeTargetLocationId), confirm_same: true };
                        const res2 = await fetch(`${API_BASE}/locations/activate`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization: sessionData.sessionKey, Username: sessionData.username }, body: JSON.stringify(body2) });
                        if (!res2.ok) { navigate('/app/home'); return; }
                        setSelectedLocation(String(closeTargetLocationId));
                        setPostCloseActivate(false);
                        setCloseTargetLocationId(null);
                      } else {
                        navigate('/app/home');
                      }
                    } catch (e) {
                      console.error('close pos error', e);
                      alert('Error al cerrar la caja');
                    }
                  }}>Confirmar</button>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  <input
                    type="text"
                    value={formatMoney((parseInt(physicalBalance || '0', 10)))}
                    onChange={(e)=>{
                      const val = parseAmount(e.target.value);
                      setPhysicalBalance(String(val));
                    }}
                    style={{ fontSize: '18px', fontWeight: 600, borderColor:'#fcbcb7', borderWidth:2, borderStyle:'solid', borderRadius:4, width: '240px', display:'block', margin:'0 auto', textAlign:'center' }}
                  />
                  <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                    <button onClick={() => { setDifferentBalance(false); }}>Atrás</button>
                    <button onClick={() => { setShowCloseModal(false); if (postCloseActivate) navigate('/app/home'); }}>Cancelar</button>
                    <button onClick={async () => {
                      const val = parseAmount(physicalBalance);
                      if (!Number.isFinite(val) || val < 0) { alert('Monto inválido'); return; }
                      try {
                        const sessionData = JSON.parse(localStorage.getItem('sessionData'));
                        const body = {
                          location_id: Number(closeTargetLocationId || selectedLocation),
                          physical_cash: val,
                          ...(postCloseActivate ? { force: true } : {})
                        };
                        const resp = await fetch(`${API_BASE}/locations/close`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization: sessionData.sessionKey, Username: sessionData.username }, body: JSON.stringify(body) });
                        if (!resp.ok) { const t = await resp.text(); alert(`No se pudo cerrar la caja. ${t}`); return; }
                        setShowCloseModal(false);
                        if (postCloseActivate && closeTargetLocationId) {
                          const body2 = { location_id: Number(closeTargetLocationId), confirm_same: true };
                          const res2 = await fetch(`${API_BASE}/locations/activate`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization: sessionData.sessionKey, Username: sessionData.username }, body: JSON.stringify(body2) });
                          if (!res2.ok) { navigate('/app/home'); return; }
                          setSelectedLocation(String(closeTargetLocationId));
                          setPostCloseActivate(false);
                          setCloseTargetLocationId(null);
                        } else {
                          navigate('/app/home');
                        }
                      } catch (e) {
                        console.error('close pos error', e);
                        alert('Error al cerrar la caja');
                      }
                    }}>Confirmar</button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.getElementById('modal-root')
        )}

      {/* Adjust cash modal */}
      {showAdjustModal &&
        createPortal(
          <div id="cart-empty-modal-overlay">
            <div id="cart-empty-modal-content">
              <h3>{adjustType === 'add' ? 'Agregar efectivo' : 'Retirar efectivo'}</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ width: 260, margin: '0 auto', textAlign: 'left' }}>
                  <label style={{ display:'block', marginBottom:4 }}>Monto</label>
                  <input
                    type="text"
                    value={formatMoney(parseInt(adjustAmount || '0', 10))}
                    onChange={(e)=>{
                      const val = parseAmount(e.target.value);
                      setAdjustAmount(String(val));
                    }}
                    style={{ fontSize:'18px', fontWeight:600, display:'block', textAlign:'left', width:'100%' }}
                  />
                </div>
                <div style={{ width: 260, margin: '0 auto', textAlign: 'left' }}>
                  <label style={{ display:'block', margin:'8px 0 4px' }}>Motivo (opcional)</label>
                  <input
                    type="text"
                    value={adjustReason}
                    onChange={(e)=> setAdjustReason(e.target.value)}
                    style={{ display:'block', width:'100%' }}
                  />
                </div>
                <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                  <button onClick={() => setShowAdjustModal(false)}>Cancelar</button>
                  <button onClick={async ()=>{
                    try {
                      const amt = parseAmount(adjustAmount);
                      if (!Number.isFinite(amt) || amt <= 0) { alert('Monto inválido'); return; }
                      const sessionData = JSON.parse(localStorage.getItem('sessionData'));
                      const body = { location_id: Number(selectedLocation), amount: amt, type: adjustType, reason: adjustReason };
                      const resp = await fetch(`${API_BASE}/locations/adjust`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization: sessionData.sessionKey, Username: sessionData.username }, body: JSON.stringify(body) });
                      const data = await resp.json();
                      if (!resp.ok || !data?.success) { alert(`No se pudo actualizar la caja. ${data?.message || ''}`); return; }
                      setShowAdjustModal(false);
                    } catch (e) {
                      console.error('adjust cash error', e);
                      alert('Error al actualizar la caja');
                    }
                  }}>Confirmar</button>
                </div>
              </div>
            </div>
          </div>,
          document.getElementById('modal-root')
        )}

      {/* Payment Modal */}
      {showPaymentModal &&
        createPortal(
          <div
            id="payment-modal-overlay"
            onClick={() => {
              setShowPaymentModal(false);
              resetPaymentState();
            }}
          >
            <div id="payment-modal-content" onClick={(e) => e.stopPropagation()}>
              <div id="left-payment-modal-content">
                <div id="payment-modal-tittle">REGISTRO DE PAGOS</div>
              </div>

              <div id="right-payment-modal-content">
                <div id="right-payment-modal-content-cash-section" className="payment-modal-inputs">
                  <label>Efectivo:</label>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <input
                      type="text"
                      value={`$${cashGiven}`}
                      onChange={(e) => {
                        const val = parseAmount(e.target.value); // integer from "$1.234" etc.
                        setCashAmount(val);                      // allow any value
                        setCashGiven(val > 0 ? val.toLocaleString("de-DE") : "");
                      }}
                      disabled={lockCash}                        // 🔒 was: disabled={false}
                      title={lockCash ? "Bloqueado: el monto con tarjeta cubre el total" : ""}
                    />

                    <button
                      id="cash-value-import-button"
                      type="button"
                      onClick={() => {
                        const remainingDueForCash = Math.max(discountedTotal - (confirmedCardTotal + cardAmount), 0);
                        setCashAmount(remainingDueForCash);
                        setCashGiven(remainingDueForCash > 0 ? remainingDueForCash.toLocaleString("de-DE") : "");
                      }}
                      disabled={
                        lockCash || Math.max(discountedTotal - (confirmedCardTotal + cardAmount), 0) <= 0
                      }
                      title={
                        lockCash
                          ? "Bloqueado: el monto con tarjeta cubre el total"
                          : "Importar el saldo pendiente a efectivo"
                      }
                      style={{ padding: "6px 10px" }}
                    >
                      <span className="material-symbols-outlined">arrow_top_left</span>
                    </button>

                  </div>
                </div>
                
                <div id="right-payment-modal-content-card-section" className="payment-modal-inputs">
                  <label>Tarjeta:</label>

                  <div className="card-two-col">
                    {/* Left column: amount input + machine selector stacked */}
                    <div className="card-col-left">
                      <input
                        type="text"
                        value={`$${cardGiven !== "" ? cardGiven : (cardAmount ? cardAmount.toLocaleString("de-DE") : "")}`}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const numeric = raw.replace(/[^\d]/g, "");
                          const parsed = parseInt(numeric || "0", 10);

                          // cap to what's allowed for card when considering cash + confirmed card
                          const maxAllowed = Math.max(discountedTotal - (cashAmount + confirmedCardTotal), 0);
                          const capped = Math.min(parsed, maxAllowed);

                          setCardAmount(capped);
                          setCardGiven(capped > 0 ? capped.toLocaleString("de-DE") : "");
                        }}
                        disabled={cardUIHardLocked}
                        title={
                          cardLocked
                            ? "Bloqueado: ya hay un pago con tarjeta confirmado"
                            : (cashCoversBill ? "Bloqueado: el efectivo ya cubre el total" : "")
                        }
                      />


                      <select
                        id="payment-machine-select"
                        disabled={manualWithoutMachine || cardLocked || cashCoversBill || machinesLoading || !paymentMachines.length}
                        value={selectedMachineId || ""}
                        onChange={(e) => setSelectedMachineId(e.target.value)}
                      >
                        {machinesLoading && <option>Cargando...</option>}
                        {!machinesLoading && !paymentMachines.length && <option>Sin máquinas</option>}
                        {!machinesLoading &&
                          paymentMachines.map((m) => (
                            <option key={m.id} value={m.id} title={`Terminal: ${m.id}`}>
                              {m.label}
                            </option>
                          ))}
                      </select>

                      <div>
                        {/* Sin ticket (solo aplica cuando se usará la máquina) */}
                        <div
                          onClick={() => { if (!manualWithoutMachine) setNoTicket(!noTicket); }}
                          onKeyDown={(e) => {
                            if (manualWithoutMachine) return;
                            if (e.key === " " || e.key === "Enter") {
                              e.preventDefault();
                              setNoTicket(!noTicket);
                            }
                          }}
                          role="checkbox"
                          aria-checked={noTicket}
                          aria-disabled={manualWithoutMachine}
                          tabIndex={0}
                          title={
                            manualWithoutMachine
                              ? "Deshabilitado: el pago se registrará sin máquina"
                              : "No imprimir ticket en la terminal"
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: manualWithoutMachine ? "not-allowed" : "pointer",
                            userSelect: "none",
                            padding: "0px 0px 0px 8px",
                            border: "1px",
                            borderRadius: "6px",
                            opacity: manualWithoutMachine ? 0.6 : 1,
                            pointerEvents: manualWithoutMachine ? "none" : "auto",
                          }}
                        >
                          {/* round indicator */}
                          <span
                            aria-hidden="true"
                            style={{
                              width: 13,
                              height: 13,
                              borderRadius: "50%",
                              border: "2px solid #fee1dd",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "border-color 120ms ease",
                              flexShrink: 0,
                            }}
                          >
                            <span
                              style={{
                                width: 5,
                                height: 5,
                                borderRadius: "50%",
                                backgroundColor: noTicket ? "#fcbcb7" : "transparent",
                                transition: "background-color 120ms ease",
                              }}
                            />
                          </span>

                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: noTicket ? "bold" : "normal",
                              transition: "color 120ms ease",
                              textAlign: "left"
                            }}
                          >
                            Sin ticket
                          </span>
                        </div>

                        {/* Registrar pago sin máquina */}
                        <div
                          onClick={() => setManualWithoutMachine(!manualWithoutMachine)}
                          onKeyDown={(e) => {
                            if (e.key === " " || e.key === "Enter") {
                              e.preventDefault();
                              setManualWithoutMachine(!manualWithoutMachine);
                            }
                          }}
                          role="checkbox"
                          aria-checked={manualWithoutMachine}
                          tabIndex={0}
                          title="Registrar la venta sin enviar cobro a la máquina"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            userSelect: "none",
                            padding: "4px 0 0 8px",
                          }}
                        >
                          <span
                            aria-hidden="true"
                            style={{
                              width: 13,
                              height: 13,
                              borderRadius: "50%",
                              border: "2px solid rgb(254, 225, 221)",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "border-color 120ms ease",
                              flexShrink: 0,
                            }}
                          >
                            <span
                              style={{
                                width: 5,
                                height: 5,
                                borderRadius: "50%",
                                backgroundColor: manualWithoutMachine ? "#999" : "transparent",
                                transition: "background-color 120ms ease",
                              }}
                            />
                          </span>
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: manualWithoutMachine ? "bold" : "normal",
                              transition: "color 120ms ease",
                              textAlign: "left"
                            }}
                          >
                            Solo registrar
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right column: buttons side by side */}
                    <div className="card-col-right">
                      <div className="card-control-buttons">
                        <div title="Importar saldo pendiente a tarjeta">
                          <button
                            id="card-value-import-button"
                            type="button"
                            onClick={() => {
                              const remainingDue = Math.max(discountedTotal - (cashAmount + confirmedCardTotal), 0);
                              setCardAmount(remainingDue);
                              setCardGiven(remainingDue > 0 ? remainingDue.toLocaleString("de-DE") : "");
                            }}
                            disabled={disableCardImport}
                            title={cashCoversBill
                              ? "Bloqueado: el efectivo cubre el total"
                              : (pendingForCardAfterTyped <= 0
                                  ? "No hay saldo pendiente para tarjeta"
                                  : "Importar el saldo pendiente a tarjeta")}
                            style={{ opacity: disableCardImport ? 0.6 : 1, pointerEvents: disableCardImport ? "none" : "auto" }}
                          >
                            <span className="material-symbols-outlined">arrow_top_left</span>
                          </button>
                        </div>
                        <div title="Enviar cobro a la máquina">
                          <button
                            type="button"
                            className="send-to-machine-btn"
                            onClick={sendCardPaymentToMachine}
                            disabled={
                              manualWithoutMachine ||
                              cardLocked ||
                              sendingPayment ||
                              machinesLoading ||
                              !paymentMachines.length ||
                              !selectedMachineId ||
                              (parseInt(cardAmount, 10) || 0) <= 0
                            }
                            aria-label="Enviar cobro a la máquina"
                          >
                            <span class="material-symbols-outlined">
                              arrow_forward_ios
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div id="right-payment-modal-content-worker-section" className="payment-modal-inputs">
                  <div id="worker-code-input-container">
                    <label>Código Kakao:</label>
                    <input
                      type="password"
                      value={workerCode}
                      onChange={(e) => setWorkerCode(e.target.value)}
                      placeholder="Ingrese código"
                      disabled={tendered >= discountedTotal}
                    />

                  </div>
                  <div className="worker-data">
                    {workerName && <div className="worker-data-details"><strong>Nombre:</strong> {workerName}</div>}
                    {workerDiscount !== null && (
                      <div className="worker-data-details">
                        <strong>Descuento:</strong> {workerDiscount}%
                      </div>
                    )}
                    {workerCodeError && <p style={{ color: "red" }}>{workerCodeError}</p>}
                  </div>
                </div>

                <div id="right-payment-modal-content-totals">
                  <div id="modal-content-totals" className="modal-content-totals-display-container">
                    <div className="modal-content-totals-display">
                      <div className="modal-content-totals-display-headers">Total:</div>
                      <div>${total.toLocaleString("de-DE")}</div>
                    </div>
                    <div className="modal-content-totals-display">
                      <div className="modal-content-totals-display-headers">
                        Descuento ({workerDiscount || 0}%):
                      </div>
                      <div>
                        ${(total * ((workerDiscount || 0) / 100)).toLocaleString("de-DE")}
                      </div>
                    </div>
                    <div className="modal-content-totals-display">
                      <div className="modal-content-totals-display-headers">Total a Pagar:</div>
                      <div>${discountedTotal.toLocaleString("de-DE")}</div>
                    </div>
                  </div>

                  <div id="modal-content-to-pay" className="modal-content-totals-display-container">
                    <div className="modal-content-totals-display">
                      <div className="modal-content-totals-display-headers">Ingresado sin Pagar:</div>
                      <div>${totalEntered.toLocaleString("de-DE")}</div>
                    </div>

                    {/* NEW: show how much card has been accredited by the terminal */}
                    <div className="modal-content-totals-display">
                      <div className="modal-content-totals-display-headers">Pagado con tarjeta: </div>
                      <div>${Number(confirmedCardTotal || 0).toLocaleString("de-DE")}</div>
                    </div>

                    {remaining > 0 ? (
                      <div className="modal-content-totals-display" style={{ color: "red" }}>
                        <div className="modal-content-totals-display-headers">Faltan:</div>
                        <div>${remaining.toLocaleString("de-DE")}</div>
                      </div>
                    ) : (
                      <div className="modal-content-totals-display" style={{ color: "green" }}>
                        <div className="modal-content-totals-display-headers">Cambio:</div>
                        <div>${Math.abs(remaining).toLocaleString("de-DE")}</div>
                      </div>
                    )}
                  </div>

                </div>

                <div id="payment-buttons">
                <button
                  disabled={submittingSale || (!canConfirm && manualWithoutMachine)}
                  aria-busy={submittingSale}
                  onClick={() => {
                    if (submittingSale) return;
                    if (!manualWithoutMachine) {
                      // Modo con máquina: solo efectivo + tarjeta aprobada
                      if ((Number(confirmedCardTotal || 0) + Number(cashAmount || 0)) < Number(discountedTotal || 0)) {
                        showModal({
                          title: "Error de pago",
                          message: "El total ingresado (efectivo + tarjeta aprobada) es menor al total a pagar.",
                          onClose: () => setShowGenericModal(false),
                        });
                        return;
                      }
                      handleSubmitSale();
                      return;
                    }

                    // Modo manual: efectivo + tarjeta (typed) + tarjeta aprobada
                    if (!canConfirm) {
                      showModal({
                        title: "Pago insuficiente",
                        message: "En modo manual, efectivo + tarjeta deben ser mayor o igual al total a pagar.",
                        onClose: () => setShowGenericModal(false),
                      });
                      return;
                    }
                    handleSubmitSale();
                  }}
                >
                  {submittingSale ? "Registrando..." : "Confirmar"}
                </button>

                  <button
                    disabled={submittingSale}
                    onClick={() => {
                      setShowPaymentModal(false);
                      setCashGiven("");
                      setCashAmount(0);
                      setCardGiven("");
                      setCardAmount(0);
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.getElementById("modal-root")
        )}

      {/* Location Change Modal (deduped) */}
      {showLocationChangeModal &&
        createPortal(
          <div id="cart-empty-modal-overlay" onClick={() => setShowLocationChangeModal(false)}>
            <div id="cart-empty-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Cambiar ubicación</h3>
              <p>Esto vaciará el carrito. ¿Deseas continuar?</p>
              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  onClick={() => {
                    setSelectedLocation(pendingLocationChange);
                    setPendingLocationChange(null);
                    setCart([]);
                    setShowLocationChangeModal(false);
                    setIsLocationDropdownOpen(false);
                  }}
                >
                  Confirmar
                </button>
                <button
                  onClick={() => {
                    setPendingLocationChange(null);
                    setShowLocationChangeModal(false);
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>,
          document.getElementById("modal-root")
        )}

      {/* Custom Product Modal */}
      {showCustomProductModal &&
        createPortal(
          <div id="cart-empty-modal-overlay" onClick={() => setShowCustomProductModal(false)}>
            <div id="cart-empty-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Agregar un Producto</h3>
              <div className="custom-product-inputs">
                <div className="custom-product-input-field">
                  <label>Descripción:</label>
                  <input
                    type="text"
                    value={customProduct.description}
                    onChange={(e) => setCustomProduct((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <div className="custom-product-price-input">
                  <label>Precio unitario ($):</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={Number(customProduct.unit_price || 0).toLocaleString("de-DE")}
                    onChange={(e) => {
                      const numeric = e.target.value.replace(/[^\d]/g, "");
                      const parsed = parseInt(numeric || "0", 10);
                      setCustomProduct((prev) => ({ ...prev, unit_price: parsed }));
                    }}
                  />
                </div>

                <div className="custom-product-input-field">
                  <label>Cantidad:</label>
                  <input
                    type="number"
                    min="1"
                    value={customProduct.reqQty}
                    onChange={(e) =>
                      setCustomProduct((prev) => ({ ...prev, reqQty: parseInt(e.target.value) || 1 }))
                    }
                  />
                </div>
              </div>

              <div className="custom-product-adding-buttons">
                <button
                  onClick={() => {
                    if (!customProduct.description || !customProduct.unit_price) return;

                    const timestamp = Date.now();
                    const metadata = { description: customProduct.description, timestamp };
                    const code = `custom-${JSON.stringify(metadata)}`;

                    const newProduct = {
                      id: `custom-${timestamp}`,
                      code,
                      description: customProduct.description,
                      unit_price: parseInt(customProduct.unit_price, 10),
                      qty: customProduct.reqQty,
                      reqQty: customProduct.reqQty,
                    };

                    setCart((prevCart) => [...prevCart, newProduct]);
                    setCustomProduct({ description: "", unit_price: "", reqQty: 1 });
                    setShowCustomProductModal(false);
                  }}
                >
                  Agregar
                </button>
                <button onClick={() => setShowCustomProductModal(false)}>Cancelar</button>
              </div>
            </div>
          </div>,
          document.getElementById("modal-root")
        )}

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </>
  );
}

export default POS;
